/**
 * Automatically selects the first available variant option when a color or other option changes.
 * This ensures that users never see an out-of-stock variant selected by default.
 */
(function() {
  'use strict';

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    // Find all variant-selects elements on the page
    const variantSelects = document.querySelectorAll('variant-selects');

    if (variantSelects.length === 0) {
      return;
    }

    variantSelects.forEach(variantSelect => {
      setupVariantAutoSelect(variantSelect);
    });

    // Also listen to Shopify's pub/sub events
    if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof subscribe !== 'undefined') {
      setupPubSubListeners();
    }
  }

  function setupPubSubListeners() {
    // Listen for variant changes from Shopify
    subscribe(PUB_SUB_EVENTS.variantChange, function(event) {
      if (!event || !event.data) return;

      const variant = event.data.variant;
      const sectionId = event.data.sectionId;

      // Find the variant-selects element for this section
      const productInfo = document.querySelector(`product-info[data-section="${sectionId}"]`);
      if (!productInfo) return;

      const variantSelect = productInfo.querySelector('variant-selects');
      if (!variantSelect) return;

      // Check if variant is available
      if (!variant || !isVariantAvailable(variant)) {
        // Wait a bit for DOM to update, then select first available option
        setTimeout(() => {
          selectFirstAvailableOptionForUnavailableVariant(variantSelect);
        }, 300);
      }
    });

    // Listen for option value selection changes
    subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, function(event) {
      if (!event || !event.data) return;

      const target = event.data.target;
      if (!target) return;

      const variantSelect = target.closest('variant-selects');
      if (!variantSelect) return;

      // Wait for Shopify to update the options (disabled/enabled states)
      setTimeout(() => {
        checkAndFixOptionSelection(variantSelect, target);
      }, 500);
    });
  }

  function setupVariantAutoSelect(variantSelect) {
    let isProcessing = false;
    let timeoutId = null;

    // Listen for changes in variant selections
    variantSelect.addEventListener('change', function(event) {
      if (isProcessing || variantSelect.dataset.autoSelecting === 'true') return;

      // Debounce to avoid multiple rapid changes
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        handleVariantChange(variantSelect, event);
      }, 100);
    });

    // Also check on initial load
    setTimeout(() => {
      checkAndFixVariantSelection(variantSelect);
    }, 500);
  }

  function handleVariantChange(variantSelect, event) {
    // Wait a bit for Shopify to update the variant data
    setTimeout(() => {
      checkAndFixVariantSelection(variantSelect, event);
    }, 400);
  }

  function checkAndFixVariantSelection(variantSelect, event) {
    const selectedVariantData = variantSelect.querySelector('[data-selected-variant]');
    if (!selectedVariantData) return;

    try {
      const variant = JSON.parse(selectedVariantData.textContent);

      // Check if the selected variant is available
      if (variant && !isVariantAvailable(variant)) {
        // Find and select the first available variant option for the remaining options
        selectFirstAvailableOption(variantSelect, event);
      }
    } catch (e) {
      // Variant data might not be updated yet, try again
      setTimeout(() => {
        checkAndFixVariantSelection(variantSelect, event);
      }, 200);
    }
  }

  function checkAndFixOptionSelection(variantSelect, changedOption) {
    // Find which option group was changed
    const optionGroups = variantSelect.querySelectorAll('fieldset, .product-form__input--dropdown');
    const optionGroupsArray = Array.from(optionGroups);
    const changedOptionGroup = changedOption.closest('fieldset, .product-form__input--dropdown');

    if (!changedOptionGroup) return;

    const changedIndex = optionGroupsArray.indexOf(changedOptionGroup);
    if (changedIndex < 0) return;

    // Check all option groups after the changed one
    for (let i = changedIndex + 1; i < optionGroupsArray.length; i++) {
      const optionGroup = optionGroupsArray[i];
      const selectedOption = getSelectedOption(optionGroup);

      // If selected option is disabled, select first available
      if (selectedOption && isOptionDisabled(selectedOption)) {
        const firstAvailable = findFirstAvailableOptionInGroup(optionGroup);
        if (firstAvailable) {
          selectOption(optionGroup, firstAvailable);
          break; // Only select one at a time, let Shopify update
        }
      }
    }
  }

  function selectFirstAvailableOptionForUnavailableVariant(variantSelect) {
    if (variantSelect.dataset.autoSelecting === 'true') return;
    variantSelect.dataset.autoSelecting = 'true';

    const optionGroups = variantSelect.querySelectorAll('fieldset, .product-form__input--dropdown');
    const optionGroupsArray = Array.from(optionGroups);

    // Find the first option group with a disabled selected option
    for (let i = 0; i < optionGroupsArray.length; i++) {
      const optionGroup = optionGroupsArray[i];
      const selectedOption = getSelectedOption(optionGroup);

      if (selectedOption && isOptionDisabled(selectedOption)) {
        const firstAvailable = findFirstAvailableOptionInGroup(optionGroup);
        if (firstAvailable) {
          selectOption(optionGroup, firstAvailable);
          setTimeout(() => {
            variantSelect.dataset.autoSelecting = 'false';
          }, 500);
          return;
        }
      }
    }

    variantSelect.dataset.autoSelecting = 'false';
  }

  function getSelectedOption(optionGroup) {
    // Get selected radio button
    const selectedRadio = optionGroup.querySelector('input[type="radio"]:checked');
    if (selectedRadio) return selectedRadio;

    // Get selected option in select
    const select = optionGroup.querySelector('select');
    if (select && select.selectedIndex >= 0) {
      return select.options[select.selectedIndex];
    }

    return null;
  }

  function isOptionDisabled(optionElement) {
    if (optionElement.classList.contains('disabled')) return true;
    if (optionElement.disabled) return true;
    if (optionElement.tagName === 'OPTION') {
      // Check if option text indicates unavailable
      const text = optionElement.textContent.trim();
      if (text.includes('unavailable') || text.includes('nincs készleten')) {
        return true;
      }
    }
    return false;
  }

  function isVariantAvailable(variant) {
    // Check if variant is available
    // Variant is available if:
    // 1. available is true, OR
    // 2. inventory_quantity > 0, OR
    // 3. inventory_policy is 'continue' (allows selling when out of stock)
    if (variant.available === true) {
      return true;
    }

    if (variant.inventory_management === 'shopify') {
      if (variant.inventory_quantity > 0) {
        return true;
      }
      if (variant.inventory_policy === 'continue') {
        return true;
      }
      return false;
    }

    // If inventory is not managed, consider it available
    return true;
  }

  function selectFirstAvailableOption(variantSelect, event) {
    // Prevent infinite loops
    if (variantSelect.dataset.autoSelecting === 'true') return;
    variantSelect.dataset.autoSelecting = 'true';

    const optionGroups = variantSelect.querySelectorAll('fieldset, .product-form__input--dropdown');
    if (optionGroups.length === 0) {
      variantSelect.dataset.autoSelecting = 'false';
      return;
    }

    // Find which option was changed
    const changedOption = event?.target?.closest('fieldset, .product-form__input--dropdown');

    // Get all option groups
    const optionGroupsArray = Array.from(optionGroups);
    const changedIndex = changedOption ? optionGroupsArray.indexOf(changedOption) : -1;

    // Start from the next option after the changed one, or from the first if we don't know
    const startIndex = changedIndex >= 0 ? changedIndex + 1 : 0;

    // For each option group after the changed one, select the first available option
    for (let i = startIndex; i < optionGroupsArray.length; i++) {
      const optionGroup = optionGroupsArray[i];
      const firstAvailable = findFirstAvailableOptionInGroup(optionGroup);

      if (firstAvailable) {
        selectOption(optionGroup, firstAvailable);
        // After selecting, wait for Shopify to update and check again
        setTimeout(() => {
          variantSelect.dataset.autoSelecting = 'false';
          // Check again after selection
          setTimeout(() => {
            checkAndFixVariantSelection(variantSelect, event);
          }, 300);
        }, 100);
        return;
      }
    }

    variantSelect.dataset.autoSelecting = 'false';
  }

  function findFirstAvailableOptionInGroup(optionGroup) {
    // Try radio buttons first
    const radioButtons = optionGroup.querySelectorAll('input[type="radio"]');
    if (radioButtons.length > 0) {
      for (const radio of radioButtons) {
        if (!isOptionDisabled(radio)) {
          return radio;
        }
      }
    }

    // Try select dropdown
    const select = optionGroup.querySelector('select');
    if (select) {
      const options = Array.from(select.options);
      for (const option of options) {
        if (!isOptionDisabled(option)) {
          // Also check if option text doesn't indicate unavailable
          const text = option.textContent.trim();
          if (!text.includes('unavailable') && !text.includes('nincs készleten')) {
            return option;
          }
        }
      }
    }

    return null;
  }

  function selectOption(optionGroup, optionElement) {
    if (optionElement.tagName === 'INPUT' && optionElement.type === 'radio') {
      // Uncheck all radios in the group first
      optionGroup.querySelectorAll('input[type="radio"]').forEach(radio => {
        radio.checked = false;
      });
      optionElement.checked = true;
      // Trigger click event as well to ensure Shopify picks it up
      optionElement.click();
      optionElement.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (optionElement.tagName === 'OPTION') {
      const select = optionElement.closest('select');
      if (select) {
        select.value = optionElement.value;
        select.selectedIndex = Array.from(select.options).indexOf(optionElement);
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }


  // Also listen for variant updates from Shopify's system via MutationObserver
  // This watches for changes to the data-selected-variant element
  function setupVariantObserver(variantSelect) {
    const selectedVariantData = variantSelect.querySelector('[data-selected-variant]');
    if (!selectedVariantData) return;

    const observer = new MutationObserver(() => {
      // Debounce the check
      clearTimeout(observer.timeoutId);
      observer.timeoutId = setTimeout(() => {
        if (variantSelect.dataset.autoSelecting !== 'true') {
          checkAndFixVariantSelection(variantSelect);
        }
      }, 200);
    });

    observer.observe(selectedVariantData, {
      childList: true,
      characterData: true,
      subtree: true
    });
  }

  // Setup observers for all variant selects
  setTimeout(() => {
    const variantSelects = document.querySelectorAll('variant-selects');
    variantSelects.forEach(variantSelect => {
      setupVariantObserver(variantSelect);
    });
  }, 1000);
})();

